/**
 * Each light's Godot size, which three's light structs lack, in one uniform array that every lit
 * program reads by the light's slot (`../lightSlots.ts`). An omni or spot light's is its
 * `light_size`, and a directional light's is the cosine offset of its `light_angular_distance`. The
 * light function widens each light's N.L by it (`lightSizeChunk.ts`). The scene's light pass
 * (`SceneShadowFitter.tsx`) writes it before each render.
 */
import { isPositionalKind, slotValues } from '../lightSlots.js';
import { LightUniformArrays } from '../shaderPatch/lightUniformArrays.js';
import { declaredLightOf } from '../directionalShadow/splitSun.js';
import { userDataDeclaration } from '../userDataDeclaration.js';
export const LIGHT_SIZES = 'godotLightSizes';
const declaration = userDataDeclaration('godotLightSize', ({ size }) => typeof size === 'number');
/** The `userData` entry that declares a light's size, to merge into its `userData`. */
export function lightSizeUserData(size) {
    return declaration.userData({ size });
}
/** One float per light. Written only by `writeLightSizeUniforms`. */
const arrays = new LightUniformArrays({ [LIGHT_SIZES]: 1 });
/** Gives the array to every built-in lit material. */
export function installLightSizeUniforms() {
    arrays.install();
}
/**
 * The light's declared size. A directional light takes its size only while Godot's directional soft
 * shadows are on (`scene_forward_clustered.glsl:2617`), and a split sun takes the size of the light
 * it shades for. A light without a declaration, such as a GLB light, has no size.
 */
function sizeOf(light, kind, drawsDirectionalSoftShadows) {
    if (!isPositionalKind(kind) && !drawsDirectionalSoftShadows)
        return 0;
    const declared = declaredLightOf(light);
    if (!declared)
        return 0;
    return declaration.read(declared)?.size ?? 0;
}
/**
 * Writes the size of each light among `slotted`. `drawsDirectionalSoftShadows` is Godot's flag for
 * the render (`sunSoftShadow.ts`).
 */
export function writeLightSizeUniforms(slotted, drawsDirectionalSoftShadows) {
    const ordered = slotValues(slotted, (light, kind) => sizeOf(light, kind, drawsDirectionalSoftShadows));
    arrays.write(ordered.length, (set) => set[LIGHT_SIZES].set(ordered));
}
//# sourceMappingURL=lightSize.js.map